import type { RouterHistory } from '@tanstack/react-router';

/**
 * What the top bar's Back / Forward arrows do when clicked (HistoryNav.tsx),
 * kept free of React so the order of checks can be tested
 * (historyArrows.test.ts).
 *
 * 0. An arrow with nowhere to go does nothing. (The buttons use
 *    aria-disabled, not the disabled attribute, so keyboard focus stays on
 *    an arrow that has just run out of entries.)
 * 1. Open work that must not be interrupted (today: an email being sent or an
 *    attachment uploading) may refuse the move: nothing happens.
 * 2. A page with unsaved changes (the form set-up editor, the landing-page
 *    editor, the Transact form fill editor) is asked about once, with its own
 *    message. Declined: nothing happens. Accepted: the step skips the page's
 *    own blocker, so the person is not asked twice. TanStack restores a
 *    blocked Forward step to the wrong entry, which is why the arrows confirm
 *    first instead of letting the page's blocker do it.
 * 3. Otherwise the arrows step exactly like the browser's own Back / Forward
 *    buttons, the mouse's side buttons and a trackpad swipe.
 */

export type HistoryArrowDirection = 'back' | 'forward';

export type HistoryArrowOutcome = 'unavailable' | 'refused' | 'declined' | 'stepped' | 'stepped-after-confirm';

export interface HistoryArrowDeps {
  /** Whether there is an entry in that direction right now (useHistoryPosition). Omitted: always. */
  canMove?: (direction: HistoryArrowDirection) => boolean;
  /** requestWorkspaceNavigation: true when open work refused the move. */
  requestMove: (request: { kind: HistoryArrowDirection; href: null; resume: () => void }) => boolean;
  /** pendingNavigationGuard: the unsaved-changes message to confirm, or null. */
  pendingGuard: () => string | null;
  confirm: (message: string) => boolean;
  /** Step without asking the page's blockers again (they were just answered). */
  stepPastBlockers: (direction: HistoryArrowDirection) => void;
  /** Step the way the browser's own button does. */
  step: (direction: HistoryArrowDirection) => void;
}

/**
 * Run one arrow click. Returns what happened right away; when open work held
 * the move and lets it go later (`resume`), the guarded step runs then, once.
 */
export function runHistoryArrow(direction: HistoryArrowDirection, deps: HistoryArrowDeps): HistoryArrowOutcome {
  if (deps.canMove && !deps.canMove(direction)) return 'unavailable';
  let settled: HistoryArrowOutcome | null = null;
  const proceed = (): HistoryArrowOutcome => {
    if (settled) return settled;
    const message = deps.pendingGuard();
    if (message !== null) {
      if (!deps.confirm(message)) {
        settled = 'declined';
        return settled;
      }
      settled = 'stepped-after-confirm';
      deps.stepPastBlockers(direction);
      return settled;
    }
    settled = 'stepped';
    deps.step(direction);
    return settled;
  };
  const refused = deps.requestMove({
    kind: direction,
    href: null,
    resume: () => {
      proceed();
    },
  });
  if (refused) return settled ?? 'refused';
  return proceed();
}

// ---- The confirmed step and the "Leave site?" prompt ---------------------------

/**
 * @tanstack/history's back()/forward() set a flag that skips the NEXT
 * beforeunload prompt, and only a beforeunload clears it. After a step inside
 * the page that event never comes, so a later reload or tab close with a
 * dirty editor would go without "Leave site?". After the confirmed step has
 * landed, a private beforeunload event is sent so TanStack's own listener
 * clears the flag (its only effect when the flag is set); other listeners do
 * not see it (installUnloadPromptResetGuard).
 */
const POP_ACTIONS: ReadonlySet<string> = new Set(['BACK', 'FORWARD', 'GO']);

/** If the step never lands (nothing in that direction after all), clear the flag after this long. */
export const UNLOAD_PROMPT_RESET_TIMEOUT_MS = 2000;

export interface UnloadPromptResetDeps {
  /** router.history.subscribe. */
  subscribe: (listener: (event: { action: { type: string } }) => void) => () => void;
  /** Clear TanStack's flag (send the private beforeunload event). */
  reset: () => void;
  setTimer: (run: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

/**
 * Clear the flag once, when the next Back / Forward / Go lands (or after the
 * timeout). A push or replace in between does not count. Returns a cancel.
 */
export function armUnloadPromptReset(deps: UnloadPromptResetDeps): () => void {
  let done = false;
  let unsubscribe: (() => void) | null = null;
  let timer: unknown = null;
  const finish = (reset: boolean) => {
    if (done) return;
    done = true;
    unsubscribe?.();
    unsubscribe = null;
    if (timer !== null) deps.clearTimer(timer);
    timer = null;
    if (reset) deps.reset();
  };
  const stop = deps.subscribe(({ action }) => {
    if (POP_ACTIONS.has(action.type)) finish(true);
  });
  if (done) stop();
  else unsubscribe = stop;
  if (!done) {
    timer = deps.setTimer(() => {
      timer = null;
      finish(true);
    }, UNLOAD_PROMPT_RESET_TIMEOUT_MS);
  }
  return () => finish(false);
}

let privateResetEvent: Event | null = null;
let guardInstalled = false;

/**
 * Keep the private reset event away from every beforeunload listener but
 * TanStack's. Call once right after createRouter: TanStack's capture listener
 * was added then, so it runs before this one, and this one stops the event
 * before any later or non-capture listener.
 */
export function installUnloadPromptResetGuard(win: Window = window): void {
  if (guardInstalled) return;
  guardInstalled = true;
  win.addEventListener(
    'beforeunload',
    (event) => {
      if (event === privateResetEvent) event.stopImmediatePropagation();
    },
    { capture: true },
  );
}

function sendUnloadPromptReset(win: Window): void {
  const event = new Event('beforeunload');
  privateResetEvent = event;
  try {
    win.dispatchEvent(event);
  } finally {
    privateResetEvent = null;
  }
}

/**
 * The arrows' step after the person confirmed leaving unsaved changes: skip
 * the page's blocker (already answered), then clear TanStack's
 * skip-the-next-prompt flag once the step has landed.
 */
export function stepPastBlockers(
  history: RouterHistory,
  direction: HistoryArrowDirection,
  win: Window = window,
): void {
  armUnloadPromptReset({
    subscribe: (listener) => history.subscribe(listener),
    reset: () => sendUnloadPromptReset(win),
    setTimer: (run, ms) => win.setTimeout(run, ms),
    clearTimer: (handle) => win.clearTimeout(handle as number),
  });
  if (direction === 'back') history.back({ ignoreBlocker: true });
  else history.forward({ ignoreBlocker: true });
}
